/**
 * 69.17 step 2: the flight model's dials (core/flightModel.ts; the owner's pick B2, semi-physical: *"I lean physical,
 * especially given that we have all these physical inputs"*). People, seconds, radians; accelerations per unit of the
 * craft's mass (people a second²), so a mass is never needed. Starting numbers, tuned toward the note's targets (BUILDLIST
 * 69.17's table): the dropship a hover craft that turns flat at about 1 rad/s, the fighter wing-borne with its tightest
 * turn about 4 rad/s near its corner speed (the 65.49 window: escaping the anti-dropship missile by manoeuvre needs ~4).
 * scripts/flightTable.ts prints the turns and the escape window these give.
 */

/** The seen gravity a craft flies against (SHIP_CRASH.fallPeople's: about a metre-scale g; the felt one, ~1,836, is the people's). */
export const FLIGHT_GRAVITY = 6;

/** The dropship: four tilting nacelles carry it, the hull is their frame (core/shipBody.ts SHIP_BODY). */
export const DROPSHIP_FLIGHT = {
  /**
   * each nacelle's place from the centre of mass (people; x left, y up, z front): front left, front right, rear left, rear
   * right (SHIP_PARTS' order). The hull's boxes put the pairs 2.39 apart (0.91 ahead of the body's middle, 1.48 behind); a
   * hover craft is trimmed with its mass between its lifters, so the centre of mass is their middle (the four at full then
   * carry it level; off it, the front pair saturated holding the nose and the ship could not lift at 60° of swing)
   */
  nacelles: [[1.69, 0.05, 1.195], [-1.69, 0.05, 1.195], [1.69, 0.05, -1.195], [-1.69, 0.05, -1.195]] as ReadonlyArray<readonly [number, number, number]>,
  /** a nacelle's thrust at full (people a second² of the whole craft): the four twice the hover's */
  thrustMax: 3,
  /** the thrust's time constant (s): a fan spools, it does not jump */
  spool: 0.25,
  /** how fast a nacelle swings (rad/s), and its range (radians from up: negative aft, π/2 level forward) */
  tiltRate: 0.9,
  tiltRange: [-0.35, 1.5] as const,
  /** the differential tilt the yaw may ask (radians each way) */
  yawTilt: 0.3,
  /** the moments of inertia per unit mass (people²): about the left axis (pitch), up (yaw), front (roll) */
  inertia: [3, 4, 2] as const,
  /** drag: a² per unit speed² along the body's left, up and front axes, and a linear share (per second) */
  drag: { quad: [0.004, 0.006, 0.0029] as const, lin: 0.05 },
  /** the rates' own damping (per second) */
  angDamp: 0.8,
  /** what the controls command: yaw, pitch and roll rates at full (rad/s) */
  rates: { yaw: 1.0, pitch: 0.5, roll: 0.8 },
  /** the attitudes the stick may hold (radians), and the rate it goes back to level when let go (per second) */
  maxPitch: 0.3,
  maxBank: 0.8,
  level: 2.5,
  /** the rate loop's gain by axis (per second): the stability augmentation turning a rate asked into a moment (2b: gentler, the yaw's most) */
  sas: { pitch: 4, roll: 4, yaw: 2 },
  /** the vertical speed Space / C ask at full (people a second, up or down: a soft touchdown under PILOTED_FLIGHT.hard), and how hard it is held (per second) */
  climb: 5,
  altHold: 1.5,
  /** 2b: the height held with the lift let go — the stiffness (per second²) and its damping (per second): critically damped */
  hold: { k: 1, d: 2 },
  /** 2b: the speed (people a second) at which A / D's yaw is half its hover's (a turn wide at cruise) */
  turnSpeed: 15,
  /** how high its gear holds the centre of mass over the ground (people) */
  gear: 2.2,
} as const;

/** The fighter: two vectoring nozzles drive it, the wing lifts it at speed, the lift jets set it down. */
export const FIGHTER_FLIGHT = {
  /** the nozzles' thrust at full throttle (people a second²: ~6.7 seen g, a space-to-atmosphere craft) */
  thrust: 40,
  /** the throttle's time constant (s) */
  spool: 0.6,
  /** the wing: lift (people a second² per people² a second²) a radian of attack, the attack it stalls past, the load limit (seen g) */
  liftK: 0.095,
  alphaMax: 0.35,
  nMax: 80,
  /** drag: the zero-lift share and the induced (per lift coefficient²), both × the speed²; the side force per radian of slip */
  cd0: 0.00055,
  induced: 5,
  side: 0.02,
  /** the commanded rates at full (rad/s: the pitch past what the wing allows, so the wing's limit is the turn's), and the speed above which the surfaces have full authority (people/s; the corner) */
  rates: { pitch: 4.5, roll: 4, yaw: 0.6 },
  corner: 120,
  /** angular acceleration the surfaces, the nozzles' vector (× throttle) and the reaction jets can give (rad/s²) */
  auth: { aero: 30, vector: 8, rcs: 2 },
  /** the weathervane: the nose pulled toward the flight path (per radian of attack or slip, × the surfaces' share) */
  stability: { alpha: 20, beta: 10 },
  sas: 10,
  /** the lift jets: their thrust (people a second²; one and a half of the seen g, the landing and the take-off), their reaction jets' authority */
  jets: { thrust: 9, rcs: 3, altHold: 1.5, climb: 4 },
  /** the nozzles' drawn vector at full pitch (radians) */
  vector: 0.3,
  gear: 1.4,
} as const;

/**
 * 69.17 step 3: a piloted ship in the Match (core/piloted.ts) — how near a person must stand to take its seat (people from
 * its foot), and its touchdowns: faster than `hard` into the ground the hull takes `hardDamage` a person a second over it
 * (ships destructible), faster than `crash` it is lost there (people a second).
 */
export const PILOTED_FLIGHT = { reachPeople: 5, hard: 6, crash: 12, hardDamage: 30 } as const;
